import React, { FC } from 'react'
import moment from 'moment'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box, Typography } from 'transfix-ui/core'
import {
  ShipperAccessorial,
  convertMinutesToInterval,
} from 'apps/marketplace/pages/shippers/utils/types/shipperAccessorial'
import { Currency } from 'components/molecules'
import { getTimeLabel } from 'utils/formatDatetime'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { DISPLAY } from 'constants/display'

interface IAccessorialRowDetailsProps {
  accessorial: ShipperAccessorial
}

/**
 * Expanded Accessorial Row with additional accessorial details
 * --
 */
const AccessorialRowDetails: FC<IAccessorialRowDetailsProps> = ({ accessorial }) => {
  const {
    approvalEmail,
    approvalMinutesLimit,
    maxAmount,
    minAmount,
    minutesThreshold,
    notes,
    notificationEmail,
    rateInterval,
    updatedAt,
  } = accessorial

  const convertMinutesThreshold = convertMinutesToInterval(minutesThreshold)
  const convertRateInterval = convertMinutesToInterval(rateInterval)

  const minutesThresholdStr = convertMinutesThreshold
    ? Object.values(convertMinutesThreshold).join(' ')
    : DISPLAY.empty
  const rateIntervalStr = convertRateInterval
    ? Object.values(convertRateInterval).join(' ')
    : DISPLAY.empty

  return (
    <Box display='flex' justifyContent='space-between'>
      {/* Section: Rate Details */}
      <Box component='section' width='30%' mr={3}>
        <Typography variant='subtitle2'>Rate Details</Typography>
        <Box mt={2}>
          <Typography variant='body2'>Charges start at {minutesThresholdStr}</Typography>
        </Box>
        <Box mt={2}>
          <Typography variant='body2'>
            Charges accrue in increments of: {rateIntervalStr}
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant='caption'>
            Min Charge:{' '}
            {minAmount ? (
              <Currency value={+minAmount} prefix='$' decimalScale={2} />
            ) : (
              DISPLAY.empty
            )}
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant='caption'>
            Max Charge:{' '}
            {maxAmount ? (
              <Currency value={+maxAmount} prefix='$' decimalScale={2} />
            ) : (
              DISPLAY.empty
            )}
          </Typography>
        </Box>
      </Box>
      {/* Section: Contact Requirements */}
      <Box component='section' width='25%' mr={2}>
        <Typography variant='subtitle2'>Contact Requirements</Typography>
        <Box mt={2}>
          <Typography variant='caption'>Notification Contact</Typography>
          <Typography variant='body2'>
            {notificationEmail ? (
              <a href={`mailto:${notificationEmail}`} target='_blank' rel='noopener noreferrer'>
                {notificationEmail}
              </a>
            ) : (
              DISPLAY.empty
            )}
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant='caption'>Approval Contact</Typography>
          <Typography variant='body2'>
            {approvalEmail ? (
              <a href={`mailto:${approvalEmail}`} target='_blank' rel='noopener noreferrer'>
                {approvalEmail}
              </a>
            ) : (
              DISPLAY.empty
            )}
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant='body2'>
            Approval requests must be <br /> sent within: {getTimeLabel(approvalMinutesLimit)}
          </Typography>
        </Box>
      </Box>
      {/* Section: Notes */}
      <Box component='section' width='25%' mr={3}>
        <Typography variant='subtitle2'>Notes</Typography>
        <Typography variant='body2'>{notes || DISPLAY.empty}</Typography>
      </Box>
      {/* Section: Last Updated */}
      <Box component='section' width='20%'>
        <Typography variant='body2'>
          Last updated{' '}
          {updatedAt && (
            <FormattedDateTime
              datetime={updatedAt}
              displayTimezone={moment.tz.guess() as AllowedTimezones}
              format='timeWeekdayDateAndYear'
              inSentence
              timeShort
              showTimezone
            />
          )}
        </Typography>
      </Box>
    </Box>
  )
}

export default AccessorialRowDetails
